import React, { createContext, lazy, Suspense, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import EntityDetailDialog from '../EntityDetailDialog';
import { getStoredSessionToken } from '../../../app/services/session.service';
import './linked-entity-card.css';

const LinkedIncomeDetailDialog = lazy(() => import('../../../modules/accounting/components/income/LinkedIncomeDetailDialog'));
const ClientDetail = lazy(() => import('../../../modules/clients/components/ConnectedClientDetailDrawer'));
const StaffDetail = lazy(() => import('../../../modules/staff/components/ConnectedStaffDetailDialog'));
const DealDetail = lazy(() => import('../../../modules/deals/shared/components/ConnectedDealDetailDialog'));
const OtherModuleDetail = lazy(() => import('./ModuleEntityDetail'));
const ORIGINAL_CARDS = {
  client: ClientDetail, staff: StaffDetail, payment: LinkedIncomeDetailDialog, invoice: LinkedIncomeDetailDialog,
  proposal: DealDetail, inProgressDeal: DealDetail, completedDeal: DealDetail,
  supplier: OtherModuleDetail, assignment: OtherModuleDetail, document: OtherModuleDetail,
  expense: OtherModuleDetail, calendarEvent: OtherModuleDetail,
};

const LinkedEntityCardContext = createContext({ openEntityCard: () => false, closeTopEntityCard: () => {} });

const ENTITY_CONFIG = {
  client: { path: '/api/clients', label: 'Cliente'},
  supplier: { path: '/api/suppliers', label: 'Fornitore'},
  staff: { path: '/api/staff', label: 'Personale'},
  assignment: { path: '/api/deals/assignments', label: 'Task'},
  proposal: { path: '/api/deals/proposals', label: 'Proposta'},
  inProgressDeal: { path: '/api/deals/in-progress', label: 'Affare in corso'},
  completedDeal: { path: '/api/deals/completed', label: 'Affare completato'},
  document: { path: '/api/documents', label: 'Documento'},
  invoice: { path: '/api/accounting/invoices', label: 'Fattura'},
  payment: { path: '/api/accounting/payments', label: 'Incasso'},
  expense: { path: '/api/accounting/expenses', label: 'Uscita'},
  calendarEvent: { path: '/api/calendar/events', label: 'Evento'},
};

const TYPE_ALIASES = {
  CLIENT: 'client', SUPPLIER: 'supplier', TEAM_MEMBER: 'staff', STAFF: 'staff', USER: 'staff',
  ASSIGNMENT: 'assignment', TASK: 'assignment', PROPOSAL: 'proposal', DEAL: 'inProgressDeal',
  IN_PROGRESS_DEAL: 'inProgressDeal', COMPLETED_DEAL: 'completedDeal', DOCUMENT: 'document',
  INVOICE: 'invoice', ACCOUNTING_INVOICE: 'invoice', PAYMENT: 'payment', ACCOUNTING_PAYMENT: 'payment',
  EXPENSE: 'expense', ACCOUNTING_EXPENSE: 'expense', CALENDAR_EVENT: 'calendarEvent',
};

export function normalizeLinkedEntityType(entityType, subtype) {
  const candidate = subtype || entityType;
  if (ENTITY_CONFIG[candidate]) return candidate;
  return TYPE_ALIASES[String(candidate || '').trim().toUpperCase()] || null;
}

async function fetchLinkedEntity(entry) {
  const config = ENTITY_CONFIG[entry.entityType];
  const token = getStoredSessionToken();
  const response = await fetch(`${config.path}/${encodeURIComponent(entry.entityId)}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (response.status === 404) throw Object.assign(new Error('Entità non trovata.'), { status: 404 });
  if (response.status === 403) throw Object.assign(new Error('Modulo disabilitato o permesso insufficiente.'), { status: 403 });
  if (!response.ok) throw Object.assign(new Error('Impossibile caricare la scheda collegata.'), { status: response.status });
  return response.json();
}

function LinkedEntityCard({ entry, onClose, onNavigateAway }) {
  const config = ENTITY_CONFIG[entry.entityType];
  if (entry.status === 'ready') {
    const OriginalCard = ORIGINAL_CARDS[entry.entityType];
    return <Suspense fallback={<EntityDetailDialog isOpen onClose={onClose} title={config.label}><p role="status">Caricamento scheda…</p></EntityDetailDialog>}>
      <OriginalCard focusComments={entry.mode === 'comments'} entity={entry.data} entityType={entry.entityType} onClose={onClose} onNavigateAway={onNavigateAway} />
    </Suspense>;
  }
  return <EntityDetailDialog isOpen onClose={onClose} title={config.label}>
    {entry.status === 'loading' ? <p role="status">Caricamento scheda…</p> : <p role="alert" className="wt-inline-error">{entry.error}</p>}
  </EntityDetailDialog>;
}

export function LinkedEntityCardProvider({ children }) {
  const [stack, setStack] = useState([]);
  const sequenceRef = useRef(0);
  const closeAllEntityCards = useCallback(() => setStack([]), []);
  useEffect(() => {
    // Module navigation must not leave a linked dialog over the new page.
    window.addEventListener('popstate', closeAllEntityCards);
    return () => window.removeEventListener('popstate', closeAllEntityCards);
  }, [closeAllEntityCards]);
  const openEntityCard = useCallback(({ entityType, entityId, subtype, mode = 'view' }) => {
    const normalizedType = normalizeLinkedEntityType(entityType, subtype);
    if (!normalizedType || entityId === null || entityId === undefined || entityId === '') return false;
    sequenceRef.current += 1;
    const key = `${normalizedType}:${entityId}:${sequenceRef.current}`;
    const entry = { key, mode, entityType: normalizedType, entityId, status: 'loading', data: null, error: '' };
    setStack((current) => [...current, entry]);
    fetchLinkedEntity(entry).then(
      (data) => setStack((current) => current.map((item) => item.key === key ? { ...item, status: 'ready', data } : item)),
      (error) => setStack((current) => current.map((item) => item.key === key ? {
        ...item, status: 'error', error: error.message, errorStatus: error.status,
      } : item))
    );
    return true;
  }, []);
  const closeTopEntityCard = useCallback(() => setStack((current) => current.slice(0, -1)), []);
  const value = useMemo(() => ({ openEntityCard, closeTopEntityCard }), [closeTopEntityCard, openEntityCard]);
  return (
    <LinkedEntityCardContext.Provider value={value}>
      {children}
      {stack.map((entry, index) => (
        <LinkedEntityCard key={entry.key} entry={entry} onNavigateAway={closeAllEntityCards} onClose={index === stack.length - 1 ? closeTopEntityCard : undefined} />
      ))}
    </LinkedEntityCardContext.Provider>
  );
}

export function useLinkedEntityCards() {
  return useContext(LinkedEntityCardContext);
}
